import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { LegalAccept } from "../components/LegalAccept";
import { useAuth } from "../context/AuthContext";
import { Field, Logo, btnPrimary, inputCls } from "../components/ui";

const Card = ({ title, sub, children, testid }) => (
  <div className="mx-auto w-full max-w-md py-6" data-testid={testid}>
    <div className="bo-card p-7">
      <div className="mb-6"><Logo /></div>
      <h1 className="text-2xl font-extrabold">{title}</h1>
      {sub && <p className="mb-5 mt-1 text-sm text-gray-500">{sub}</p>}
      {children}
    </div>
  </div>
);

const ErrorBox = ({ msg }) => msg ? <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="auth-error">{msg}</p> : null;

const homeFor = (u) => (u.is_staff ? "/admin" : { store: "/painel-loja", courier: "/estafeta" }[u.role] || "/");

function MfaStep({ token, onDone }) {
  const { verifyMfa } = useAuth();
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { onDone(await verifyMfa(token, code.trim())); } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Card title="Verificação em dois passos" sub="Introduza o código de 6 dígitos da sua aplicação autenticadora ou um código de recuperação." testid="mfa-login-page">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Código"><input autoFocus required autoComplete="one-time-code" maxLength={20} value={code} onChange={(e) => setCode(e.target.value)} className={`${inputCls} text-center text-lg tracking-[0.3em]`} data-testid="mfa-login-code-input" /></Field>
        <ErrorBox msg={err} />
        <button disabled={busy || code.trim().length < 6} className={btnPrimary} data-testid="mfa-login-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Verificar</button>
      </form>
    </Card>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const done = (u) => {
    toast.success(`Bem-vindo(a), ${u.name.split(" ")[0]}`);
    nav(params.get("next") || homeFor(u));
  };
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const u = await login(f.email, f.password);
      if (u.mfa_required) setMfaToken(u.mfa_token); else done(u);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  const isAdmin = params.get("next") === "/admin";
  if (mfaToken) return <MfaStep token={mfaToken} onDone={done} />;
  return (
    <Card title={isAdmin ? "Entrar na Administração" : "Entrar"} sub={isAdmin ? "Acesso reservado à equipa BÔ Market." : "Aceda à sua conta BÔ Market."} testid="login-page">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Email"><input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="login-email-input" /></Field>
        <Field label="Palavra-passe"><input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="login-password-input" /></Field>
        <ErrorBox msg={err} />
        <button disabled={busy} className={btnPrimary} data-testid="login-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Entrar</button>
        <div className="flex justify-between text-sm">
          <Link to="/recuperar-palavra-passe" className="text-bo-green hover:underline" data-testid="forgot-password-link">Esqueci a palavra-passe</Link>
          <Link to="/registar" className="font-semibold text-bo-green hover:underline" data-testid="register-link">Criar conta</Link>
        </div>
        {!isAdmin && <Link to="/entrar?next=/admin" className="pt-2 text-center text-xs text-gray-400 hover:text-gray-600" data-testid="login-admin-link">Entrar na Administração</Link>}
      </form>
    </Card>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await register({ ...f, phone: f.phone || null, accept_legal: true });
      toast.success("Conta criada. Enviámos um email de confirmação.");
      nav("/conta/perfil");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Card title="Criar conta" sub="Registe-se para comprar e acompanhar as suas encomendas." testid="register-page">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Nome completo"><input required minLength={2} value={f.name} onChange={set("name")} className={inputCls} data-testid="register-name-input" /></Field>
        <Field label="Email"><input type="email" required value={f.email} onChange={set("email")} className={inputCls} data-testid="register-email-input" /></Field>
        <Field label="Telefone (opcional)"><input value={f.phone} onChange={set("phone")} placeholder="+239 ..." className={inputCls} data-testid="register-phone-input" /></Field>
        <Field label="Palavra-passe"><input type="password" required minLength={8} value={f.password} onChange={set("password")} className={inputCls} data-testid="register-password-input" /></Field>
        <p className="-mt-2 text-xs text-gray-500">Mínimo 8 caracteres, com letras e números.</p>
        <ErrorBox msg={err} />
        <LegalAccept context="register" />
        <button disabled={busy} className={btnPrimary} data-testid="register-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Criar conta</button>
        <p className="text-center text-sm">Já tem conta? <Link to="/entrar" className="font-semibold text-bo-green hover:underline" data-testid="goto-login-link">Entrar</Link></p>
      </form>
    </Card>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const { reload } = useAuth();
  const [state, setState] = useState("loading");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(() => { setState("ok"); reload(); }).catch(() => setState("error"));
  }, [params, reload]);
  return (
    <Card title="Confirmação de email" testid="verify-email-page">
      {state === "loading" && <Loader2 className="h-6 w-6 animate-spin text-bo-green" />}
      {state === "ok" && <p className="flex items-center gap-2 text-bo-green" data-testid="verify-success"><CheckCircle2 className="h-5 w-5" />Email confirmado. Já pode fazer encomendas.</p>}
      {state === "error" && <p className="flex items-center gap-2 text-red-600" data-testid="verify-error"><XCircle className="h-5 w-5" />Link inválido ou expirado. Peça um novo no seu perfil.</p>}
      <Link to="/" className={`${btnPrimary} mt-6 w-full`} data-testid="verify-home-btn">Ir para o início</Link>
    </Card>
  );
}

export function ActivateAccount() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/auth/activate", { token: params.get("token") || "", password: pw });
      toast.success("Conta ativada. Entre com a sua palavra-passe.");
      nav("/entrar");
    } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <Card title="Ativar conta" sub="Defina a sua palavra-passe para ativar o acesso." testid="activate-page">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Palavra-passe"><input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="activate-password-input" /></Field>
        <p className="-mt-2 text-xs text-gray-500">Mínimo 8 caracteres, com letras e números.</p>
        <ErrorBox msg={err} />
        <button className={btnPrimary} data-testid="activate-submit-btn">Ativar conta</button>
      </form>
    </Card>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <Card title="Recuperar palavra-passe" sub="Enviaremos um link temporário para o seu email." testid="forgot-page">
      {sent ? <p className="rounded-xl bg-bo-green/10 p-3 text-sm text-bo-green" data-testid="forgot-sent">Se o email estiver registado, receberá as instruções em breve. O link é válido durante 1 hora.</p> : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label="Email"><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="forgot-email-input" /></Field>
          <ErrorBox msg={err} />
          <button className={btnPrimary} data-testid="forgot-submit-btn">Enviar link</button>
        </form>
      )}
    </Card>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw });
      toast.success("Palavra-passe alterada. Entre com a nova palavra-passe.");
      nav("/entrar");
    } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <Card title="Nova palavra-passe" testid="reset-page">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Nova palavra-passe"><input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="reset-password-input" /></Field>
        <ErrorBox msg={err} />
        <button className={btnPrimary} data-testid="reset-submit-btn">Guardar</button>
      </form>
    </Card>
  );
}
